import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

// ─── Interfaces ──────────────────────────────────────────────

/**
 * Ledger line within a journal entry.
 */
export interface LedgerLine {
  id: string;
  journal_entry: string;
  journal_entry_id: string;
  account: string;
  account_code: string;
  account_name: string;
  account_type: string;
  amount: string;
  side: 'DEBIT' | 'CREDIT';
  side_display: string;
  currency: string;
  currency_code: string;
  description: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Minimal journal entry (used in nested references).
 */
export interface JournalEntryMinimal {
  id: string;
  event_type: string;
  event_type_display: string;
  transaction_reference: string;
  description: string;
  created_at: string;
}

/**
 * Full journal entry with ledger lines.
 */
export interface JournalEntry {
  id: string;
  event_type: string;
  event_type_display: string;
  transaction_reference: string;
  description: string;
  metadata: Record<string, unknown>;
  content_type: string | null;
  object_id: string | null;
  transaction_object: { type: string; id: string } | null;
  transaction_type: string | null;
  ledger_lines: LedgerLine[];
  ledger_lines_count: number;
  is_balanced: boolean;
  total_debits: Record<string, string>;
  total_credits: Record<string, string>;
  is_reversed: boolean;
  reversed_entry: JournalEntryMinimal | null;
  reversal_entry: JournalEntryMinimal | null;
  created_at: string;
  updated_at: string;
}

/**
 * Lightweight journal entry for list views.
 */
export interface JournalEntryListItem {
  id: string;
  event_type: string;
  event_type_display: string;
  transaction_reference: string;
  description: string;
  transaction_type: string | null;
  ledger_lines_count: number;
  created_at: string;
}

/**
 * Journal entry statistics.
 */
export interface JournalStats {
  total_entries: number;
  recent_entries: number;
  entries_by_type: { event_type: string; count: number }[];
  total_by_currency: Record<string, { debit: string; credit: string }>;
}

/**
 * Account search result with balance for journal form autocomplete.
 */
export interface AccountSearchResult {
  id: string;
  code: string;
  name: string;
  account_type: string;
  account_type_display: string;
  currency_code: string;
  currency_id: string;
  balance: string;
  is_system: boolean;
}

/**
 * Payload for creating a journal entry.
 */
export interface JournalEntryCreatePayload {
  description: string;
  event_type: string;
  debit_credits: {
    account_id: string;
    amount: string;
    side: 'DEBIT' | 'CREDIT';
    description?: string;
  }[];
  metadata?: Record<string, unknown>;
}

/**
 * Reverse response from the API.
 */
export interface ReverseResponse {
  message: string;
  reversal_entry: JournalEntry;
}

// ─── Service ─────────────────────────────────────────────────

/**
 * Service for managing Journal Entries and Ledger operations.
 */
@Injectable({
  providedIn: 'root',
})
export class JournalService {
  private readonly api = inject(ApiService);
  private readonly journalPath = 'finances/journal-entries';
  private readonly accountPath = 'finances/accounts';

  // ─── Journal Entries ────────────────────────────────────────

  /**
   * Get a single journal entry by ID (full detail with lines).
   */
  getJournalEntry(id: string): Observable<JournalEntry> {
    return this.api.get<JournalEntry>(`${this.journalPath}/${id}/`);
  }

  /**
   * Get ledger lines for a specific journal entry.
   */
  getJournalEntryLines(id: string): Observable<LedgerLine[]> {
    return this.api.get<LedgerLine[]>(`${this.journalPath}/${id}/lines/`);
  }

  /**
   * Get journal entry statistics.
   */
  getStats(days: number = 30): Observable<JournalStats> {
    return this.api.get<JournalStats>(`${this.journalPath}/stats/`, { days: days.toString() });
  }

  /**
   * Create a new journal entry (manual operation).
   */
  createJournalEntry(payload: JournalEntryCreatePayload): Observable<JournalEntry> {
    return this.api.post<JournalEntry>(`${this.journalPath}/`, payload);
  }

  /**
   * Reverse a journal entry.
   */
  reverseJournalEntry(id: string): Observable<ReverseResponse> {
    return this.api.post<ReverseResponse>(`${this.journalPath}/${id}/reverse/`, {});
  }

  // ─── Account Search ────────────────────────────────────────

  /**
   * Search accounts with balance information for journal form autocomplete.
   */
  searchAccounts(
    query: string,
    options?: { account_type?: string; currency?: string; limit?: number },
  ): Observable<AccountSearchResult[]> {
    const params: Record<string, string> = { q: query };
    if (options?.account_type) params['account_type'] = options.account_type;
    if (options?.currency) params['currency'] = options.currency;
    if (options?.limit) params['limit'] = options.limit.toString();
    return this.api.get<AccountSearchResult[]>(`${this.accountPath}/search-with-balance/`, params);
  }

  /**
   * Get a single account's balance (for refreshing).
   */
  getAccountBalance(accountId: string): Observable<{ balance: string; currency: string }> {
    return this.api.get<{ balance: string; currency: string }>(`${this.accountPath}/${accountId}/balance/`);
  }
}
